<template>
  <IntersectionObserver :options="intersectionOptions" @intersect="onIntersect">
    <div ref="containerRef" class="npo-player-placeholder">
      <Teleport to="body" :disabled="!isMiniPlayerActive">
        <div class="npo-player" :class="{ 'mini-player': isMiniPlayerActive }">
          <div ref="playerContainer" class="player-container" />
        </div>
      </Teleport>
    </div>
  </IntersectionObserver>
</template>

<script setup lang="ts">
import { useNpoPlayerSetup } from '~/composables/useNpoPlayerSetup';
import { useThrottleFn } from '@vueuse/core';
import useNpoPlayerEmbed from '~/composables/useNpoPlayerEmbed';
import type { TPlayerStatus } from '~~/types/local_types';
import formatUtils from '~/utils/formatUtils';

// --- Props & Emits ---
interface IProps {
  videoId: string;
  initialState?: TPlayerStatus;
  duration?: string;
}

const { videoId, initialState = 'paused', duration = undefined } = defineProps<IProps>();
const instanceId = useId();

const emit = defineEmits<{
  loaded: [sourceConfig: any];
}>();

// --- Composables & Stores ---
const config = useRuntimeConfig();
const videoStore = useVideoStore();
const { initializePlayer, destroyPlayer, play, pause, currentState, currentViewMode } =
  useNpoPlayerSetup();
const { formatIsoDurationNoZeros } = formatUtils();

// --- UI & Player State ---
const containerRef = ref<HTMLElement>();
const playerContainer = ref<HTMLElement>();

const viewModeChangedByScrolling = ref(false);
const isManualPiP = ref(false);
const videoIsVisible = ref(false);
// --- Computed ---
const intersectionOptions = computed(() => ({ threshold: 0.75 }));

/**
 * Logic to determine if the player should be in the fixed "mini" position.
 * Only triggers if it's the active video and either scroll or manual PiP is active.
 */
const isMiniPlayerActive = computed(() => {
  const isThisVideoActive =
    videoStore.activeVideoId === videoId && videoStore.activeInstanceId === instanceId;
  return (viewModeChangedByScrolling.value || isManualPiP.value) && isThisVideoActive;
});

const fetchToken = async () => {
  try {
    const data = await $fetch(`/api/token`, {
      query: { videoId, t: Date.now() }, // Cache busting via query instead of heavy headers
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    return data;
  } catch (err) {
    console.error('Token fetch failed:', err);
    // throw err;
  }
};

const throttledFetchToken = useThrottleFn(fetchToken, 500);

// --- Logic: Store Synchronization ---

// Watch for global active changes: If another video starts, pause THIS one.
watch(
  [() => videoStore.activeVideoId, () => videoStore.activeInstanceId],
  ([newVideoId, newInstanceId]) => {
    if (newVideoId !== videoId || newInstanceId !== instanceId) {
      if (currentState.value === 'playing') {
        pause();
        viewModeChangedByScrolling.value = false;
      }
    }
  },
);

/**
 * Entering real (native) fullscreen must always exit our custom mini-player
 * mode: while `.mini-player` is active, the player's own top/middle/bottom
 * bars are CSS-hidden unless hovered — but fullscreen doesn't remove the
 * element from under that ancestor class, so without this the controls stay
 * hidden once you go PiP -> fullscreen.
 */
watch(currentViewMode, (mode) => {
  if (mode === 'fullscreen' && (isManualPiP.value || viewModeChangedByScrolling.value)) {
    isManualPiP.value = false;
    viewModeChangedByScrolling.value = false;
    videoStore.setMode('inline');
  }
});

/**
 * Handles the NPO player UI PiP button clicks manually to override
 * default browser behavior with our Teleport-based mini-player.
 */
const handleInternalPipClick = (event: MouseEvent) => {
  const pipButton = (event.target as HTMLElement).closest('.npoplayer-pip-button');
  if (!pipButton) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  isManualPiP.value = !isManualPiP.value;

  if (isManualPiP.value) {
    viewModeChangedByScrolling.value = false;
    videoStore.setMode('pictureinpicture');
    videoStore.playVideo(videoId, instanceId);
  } else {
    videoStore.setMode('inline');
    if (!videoIsVisible.value) pause();
  }
};

/**
 * Sets up NPO player event listeners.
 * Note: native PiP is fully intercepted at the click level (handleInternalPipClick),
 * so there's no need to react to/undo displayMode changes here.
 */
const setupPlayerListeners = (playerInstance: any) => {
  playerInstance.on('play', () => {
    videoStore.playVideo(videoId, instanceId);
  });
};

// --- Lifecycle ---

onMounted(async () => {
  // 1. Listen for clicks on the player container (capturing phase)
  // We keep this, but ensure it only acts on the PiP button
  playerContainer.value?.addEventListener('click', handleInternalPipClick, true);

  // 2. Initialize the core player
  const durationLabel = duration ? formatIsoDurationNoZeros(duration) : undefined;
  const player = await initializePlayer(playerContainer.value, initialState, durationLabel);
  if (!player) return;

  setupPlayerListeners(player);

  // 3. Fetch Stream Token
  const token = (await throttledFetchToken()) as string;
  if (!token) return;

  // 4. Load the stream
  player
    .load(token, {
      endpoint: config.public.npoPlayerEndpoint,
    })
    .then(() => {
      useNpoPlayerEmbed(player, videoId);
      emit('loaded', {});
    })
    .catch((err) => {
      console.error('Error loading stream:', err);
    });

  /**
   * BUG FIX: Only call play() if explicitly requested.
   * Do NOT call pause() here, as the player is paused by default.
   * Calling pause() at the end of the async chain often overrides
   * a user's manual "Play" click that happened during the loading phase.
   */
  if (initialState === 'playing') {
    play();
  }

  // Handle subsequent prop changes
  watch(
    () => initialState,
    (val) => {
      val === 'playing' ? play() : pause();
    },
    { immediate: true },
  );
});

onBeforeUnmount(() => {
  if (videoStore.activeVideoId === videoId && videoStore.activeInstanceId === instanceId) {
    videoStore.reset();
  }
  destroyPlayer();
});

// --- Intersection Handler ---

const onIntersect = ({ visible }: { visible: boolean }) => {
  videoIsVisible.value = visible;
  const isPlaying = currentState.value === 'playing';
  const isThisActive =
    videoStore.activeVideoId === videoId && videoStore.activeInstanceId === instanceId;

  // Logic for Auto-PiP on scroll
  if (!visible && isPlaying && isThisActive && !isManualPiP.value) {
    viewModeChangedByScrolling.value = true;
    videoStore.setMode('pictureinpicture');
  } else if (visible && viewModeChangedByScrolling.value) {
    viewModeChangedByScrolling.value = false;
    videoStore.setMode('inline');
  }
};
</script>

<style lang="postcss">
@import '../assets/style/vendors/npo-player.pcss';
</style>

<style lang="postcss" scoped>
/* Placeholder keeps the 16:9 space in the article flow
  to prevent layout shifts when the player is teleported to the body.
*/
.npo-player-placeholder {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: hsl(var(--color-primary-100));

  &::after {
    position: absolute;
    content: '';
    inset: 0;
    margin: auto;
    width: auto;
    height: 40%;
    aspect-ratio: 1/1;
    max-height: 200px;
    background: url('data:image/svg+xml;base64,PHN2ZyBmaWxsPSJub25lIiBoZWlnaHQ9IjE0NSIgdmlld0JveD0iMCAwIDIyNyAxNDUiIHdpZHRoPSIyMjciIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuOCI+PHBhdGggZD0ibTY4LjA1NDYuMDI1NTEyN3YxNDQuNjAwNDg3M2gtMzQuODc2NHYtMTExLjk3OTloLS44NjE4bC0zMi4zMTY0IDE5Ljc3MDF2LTMwLjIxMjhsMzUuNjM2OC0yMi4xNzc4ODczeiIvPjxwYXRoIGQ9Im0xNzcuNzI4IDE0NC42MjZoLTM5LjU0bC00OC43OTEzLTE0NC42MDA1ODY1aDQxLjU0MjNjLS4yMi0uMjM0OTY1NSAxNS41MjIgNDguMzQzNzg2NSAyOC44MDMgODkuMjUwNTg2NS43MjQgMi4yMjg1IDE3Ljk4NiA1NS4zNSAxNy45ODYgNTUuMzV6Ii8+PHBhdGggZD0ibTE4Ny40ODcuMDAwMDYxMDRoMzkuMTA5bC00OC44NjggMTQ0LjYyNTkzODk2LTE2LjAzNi00OS4zNDQ0Yy0uMjg0LS44NzQyLTEuODEtNS41NzM2LTEuOTUtNi4wMDU2LjAxNS4wNDc1LjAzMy0uMTA1NSAwIDAgMTIuNjk0LTQwLjc1MDYgMjcuNzQ1LTg5LjI3NTkzODk2IDI3Ljc0NS04OS4yNzU5Mzg5NnoiLz48L2c+PC9zdmc+')
      center/100% no-repeat;
    z-index: 0;
  }
}

.npo-player {
  position: relative;
  width: 100%;
  height: 100%;
  background: hsl(var(--color-primary-100));

  .player-container {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 10;

    &:after {
      position: absolute;
      display: block;
      pointer-events: none;
      inset: 0;
      content: '';
      border: 1px solid hsla(var(--color-primary-950), 0.075);
      mix-blend-mode: multiply;
    }
  }

  /* Fixed Mini Player State */
  &.mini-player {
    position: fixed;
    bottom: var(--unit-14);
    right: var(--unit-14);
    width: calc(100vw / 2 - var(--unit-14));
    height: auto;
    aspect-ratio: 16 / 9;
    z-index: 9999;
    box-shadow: var(--effect-box-shadow-primary);
    overflow: hidden;

    @media (--md-viewport) {
      min-width: 320px;
      width: calc(100vw / 3 - var(--unit-14));
    }

    @media (--lg-viewport) {
      max-width: 400px;
    }

    /* Hide the NPO player UI chrome by default in mini-player, show on hover */
    :deep(.npoplayer-top-bar),
    :deep(.npoplayer-middle-bar),
    :deep(.npoplayer-bottom-bar) {
      opacity: 0;
      visibility: hidden;
      transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
      pointer-events: none;
    }

    &:hover {
      :deep(.npoplayer-top-bar),
      :deep(.npoplayer-middle-bar),
      :deep(.npoplayer-bottom-bar) {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }
    }

    /*
     * These don't make sense (or don't work) while floating in mini-player mode:
     * fullscreen from here can't restore its own controls afterwards (no room to
     * fix that up client-side), and settings/embed just add clutter to a tiny box.
     */
    :deep(.npoplayer-settings-toggle-button),
    :deep(.npoplayer-embed-button),
    :deep(.npoplayer-fullscreen-button) {
      display: none !important;
    }
  }

  &::before,
  &::after {
    display: block;
    content: '';
    position: absolute;
    pointer-events: none;
  }

  &::before {
    inset: 0;
    margin: auto;
    width: auto;
    height: 40%;
    aspect-ratio: 1/1;
    max-height: 200px;
    background: url('data:image/svg+xml;base64,...') center/100% no-repeat;
    z-index: 0;
    opacity: 0.8;
  }

  &::after {
    inset: 0;
    background: linear-gradient(
      116.4deg,
      hsl(var(--color-primary-100), 0) 36.86%,
      hsl(var(--color-primary-100), 0.35) 45.37%,
      hsl(var(--color-primary-100), 0.5) 53.89%,
      hsl(var(--color-primary-100), 0.35) 62.4%,
      hsl(var(--color-primary-100), 0) 70.91%
    );
    animation: background-swoosh 2s 1s ease-in-out infinite;
    background-size: 300%;
    z-index: 1;
  }
}
</style>

<style lang="postcss">
/*
 * Scoped to our own play-button, NOT a bare ".duration" — this class name
 * collides with other, unrelated video components (e.g. the INOS player's
 * own progress-bar track also uses ".duration"), and this style block is
 * global (Vue <style> here isn't `scoped`), so an unscoped selector would
 * bleed onto and corrupt any other ".duration" element on the page.
 */
.npoplayer-middle-bar .npoplayer-play-button .duration {
  /* .npoplayer-play-button hides its own native label via text-indent: 100% —
     text-indent is inherited, so reset it here or our own text inherits the same offset. */
  text-indent: 0;
  white-space: normal;

  position: absolute;
  top: calc(50% + (var(--npo-player-playbutton-height) / 2));
  left: 50%;
  width: var(--npo-player-playbutton-width);
  text-align: center;
  transform: translate(-50%, 0);
  background: var(--effect-gradient-primary);
  border-radius: 0 0 var(--unit-8) var(--unit-8);
  @add-mixin label-lg-semi-bold;
  padding: var(--unit-3) var(--unit-4) var(--unit-1) var(--unit-4);
  color: hsl(var(--color-neutral-white));
  z-index: 1;

  @media (--md-viewport) {
    padding-top: var(--unit-3);
  }

  @media (--lg-viewport) {
    padding-top: var(--unit-5);
  }
}
</style>